import { Menu } from "@headlessui/react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../Views/Logo";

import {
  MenuIcon,
  TicketIcon,
  ShieldCheckIcon,
  ClockIcon,
  DeviceMobileIcon,
  CashIcon,
  ChartBarIcon,
  UserGroupIcon,
  PrinterIcon,
  RefreshIcon,
  TruckIcon,
  DatabaseIcon,
  LocationMarkerIcon,
  PhoneIcon,
  MailIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ViewGridIcon,
  CreditCardIcon,
  UserIcon,
} from "@heroicons/react/outline";

function Home() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-gray-800">

      {/* ===================================================== */}
      {/* NAVBAR */}
      {/* ===================================================== */}

      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="flex items-center justify-between h-16">

            {/* Logo */}
            <button
              type="button"
              onClick={() => navigate("/")}
              className="flex items-center focus:outline-none"
              aria-label="Go to home"
            >
              <Logo />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">

              <a
                href="#features"
                className="
                  px-4 py-2
                  text-sm font-medium
                  text-gray-600
                  hover:text-[#80b643]
                  hover:bg-[#80b643]/5
                  rounded-lg
                  transition
                "
              >
                Features
              </a>

              <a
                href="#benefits"
                className="
                  px-4 py-2
                  text-sm font-medium
                  text-gray-600
                  hover:text-[#80b643]
                  hover:bg-[#80b643]/5
                  rounded-lg
                  transition
                "
              >
                Benefits
              </a>

              <a
                href="#contact"
                className="
                  px-4 py-2
                  text-sm font-medium
                  text-gray-600
                  hover:text-[#80b643]
                  hover:bg-[#80b643]/5
                  rounded-lg
                  transition
                "
              >
                Contact
              </a>

              <Link
                to="/login"
                className="
                  ml-3
                  inline-flex items-center justify-center
                  px-5 py-2.5
                  bg-[#80b643]
                  text-white
                  rounded-xl
                  text-sm font-semibold
                  hover:bg-[#719f3a]
                  transition
                  shadow-sm
                "
              >
                Login
              </Link>

            </nav>

            {/* Mobile Menu */}
            <div className="md:hidden">

              <Menu as="div" className="relative">

                <Menu.Button
                  className="
                    flex items-center justify-center
                    w-10 h-10
                    rounded-xl
                    bg-gray-100
                    text-gray-700
                    hover:bg-[#80b643]/10
                    hover:text-[#80b643]
                    transition
                    focus:outline-none
                  "
                  aria-label="Open menu"
                >
                  <MenuIcon className="h-6 w-6" />
                </Menu.Button>

                <Menu.Items
                  className="
                    absolute right-0 mt-2
                    w-56
                    origin-top-right
                    bg-white
                    rounded-xl
                    shadow-xl
                    border border-gray-200
                    overflow-hidden
                    focus:outline-none
                    z-50
                  "
                >

                  <div className="p-2">

                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="#features"
                          className={`
                            block px-4 py-3 rounded-lg
                            text-sm font-medium text-gray-700
                            ${active ? "bg-gray-100 text-[#80b643]" : ""}
                          `}
                        >
                          Features
                        </a>
                      )}
                    </Menu.Item>

                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="#benefits"
                          className={`
                            block px-4 py-3 rounded-lg
                            text-sm font-medium text-gray-700
                            ${active ? "bg-gray-100 text-[#80b643]" : ""}
                          `}
                        >
                          Benefits
                        </a>
                      )}
                    </Menu.Item>

                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="#contact"
                          className={`
                            block px-4 py-3 rounded-lg
                            text-sm font-medium text-gray-700
                            ${active ? "bg-gray-100 text-[#80b643]" : ""}
                          `}
                        >
                          Contact
                        </a>
                      )}
                    </Menu.Item>

                    <div className="my-2 border-t border-gray-100" />

                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/login"
                          className={`
                            block px-4 py-3 rounded-lg
                            text-sm font-semibold text-[#80b643]
                            ${active ? "bg-[#80b643]/10" : ""}
                          `}
                        >
                          Login
                        </Link>
                      )}
                    </Menu.Item>

                  </div>

                </Menu.Items>

              </Menu>

            </div>

          </div>

        </div>

      </header>


      {/* ===================================================== */}
      {/* HERO */}
      {/* ===================================================== */}

      <section className="relative overflow-hidden bg-gray-50">

        {/* Decorative elements */}

        <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-[#80b643]/5 rounded-full blur-3xl" />

        <div className="absolute bottom-0 left-0 w-[24rem] h-[24rem] bg-[#80b643]/5 rounded-full blur-3xl" />


        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 sm:py-20 lg:py-24">

            {/* LEFT SIDE */}

            <div>

              {/* Badge */}

              <div
                className="
                  inline-flex items-center gap-2
                  bg-[#80b643]/10
                  border border-[#80b643]/20
                  text-[#80b643]
                  px-4 py-2
                  rounded-full
                  text-xs sm:text-sm
                  font-semibold
                  mb-6
                "
              >
                <TruckIcon className="h-4 w-4" />

                Transport Management Platform
              </div>


              {/* Heading */}

              <h1
                className="
                  text-4xl
                  sm:text-5xl
                  lg:text-6xl
                  font-bold
                  tracking-tight
                  text-gray-900
                  leading-[1.08]
                "
              >
                Smarter Ticketing.

                <span className="block text-[#80b643]">
                  Better Transport Management.
                </span>
              </h1>


              {/* Description */}

              <p
                className="
                  mt-6
                  text-base
                  sm:text-lg
                  text-gray-600
                  max-w-xl
                  leading-relaxed
                "
              >
                TellerPoint gives transport companies one powerful platform
                to manage ticket sales, seats, payments, employees, routes
                and business reporting.
              </p>


              {/* CTA */}

              <div className="mt-8 flex flex-col sm:flex-row gap-3">

                <Link
                  to="/user/register"
                  className="
                    inline-flex items-center justify-center gap-2
                    px-6 py-3.5
                    bg-[#80b643]
                    text-white
                    rounded-xl
                    font-semibold
                    shadow-sm
                    hover:bg-[#719f3a]
                    transition
                  "
                >
                  Get Started
                  <ArrowRightIcon className="h-5 w-5" />
                </Link>

                <a
                  href="#features"
                  className="
                    inline-flex items-center justify-center
                    px-6 py-3.5
                    bg-white
                    border border-gray-200
                    text-gray-700
                    rounded-xl
                    font-semibold
                    hover:border-[#80b643]
                    hover:text-[#80b643]
                    transition
                  "
                >
                  Explore Features
                </a>

              </div>


              {/* Trust Points */}

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">

                <TrustPoint text="Fast Ticket Sales" />

                <TrustPoint text="Real-Time Operations" />

                <TrustPoint text="Business Reporting" />

              </div>

            </div>


            {/* RIGHT SIDE - PRODUCT PREVIEW */}

            <div className="relative">

              {/* Main Dashboard Card */}

              <div
                className="
                  relative
                  bg-white
                  border border-gray-200
                  rounded-2xl
                  shadow-xl
                  overflow-hidden
                "
              >

                {/* Dashboard Header */}

                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">

                  <div className="flex items-center gap-3">

                    <div className="w-9 h-9 rounded-xl bg-[#80b643]/10 flex items-center justify-center">
                      <TicketIcon className="w-5 h-5 text-[#80b643]" />
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        TellerPoint POS
                      </p>

                      <p className="text-xs text-gray-500">
                        Transport Dashboard
                      </p>
                    </div>

                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#80b643]" />
                    <span className="text-xs text-gray-500">
                      Online
                    </span>
                  </div>

                </div>


                {/* Dashboard Body */}

                <div className="p-5">

                  {/* Summary Cards */}

                  <div className="grid grid-cols-3 gap-3">

                    <DashboardMiniCard
                      label="Tickets"
                      value="1,248"
                      icon={<TicketIcon className="w-4 h-4" />}
                    />

                    <DashboardMiniCard
                      label="Revenue"
                      value="K86.4K"
                      icon={<CashIcon className="w-4 h-4" />}
                    />

                    <DashboardMiniCard
                      label="Routes"
                      value="24"
                      icon={<LocationMarkerIcon className="w-4 h-4" />}
                    />

                  </div>


                  {/* Chart */}

                  <div className="mt-5 border border-gray-200 rounded-xl p-4">

                    <div className="flex items-center justify-between">

                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          Sales Overview
                        </p>

                        <p className="text-xs text-gray-500 mt-0.5">
                          Ticket sales performance
                        </p>
                      </div>

                      <ChartBarIcon className="w-5 h-5 text-[#80b643]" />

                    </div>


                    {/* Fake chart */}

                    <div className="mt-5 flex items-end gap-2 h-28">

                      <div className="flex-1 bg-[#80b643]/10 rounded-t-md h-[35%]" />
                      <div className="flex-1 bg-[#80b643]/20 rounded-t-md h-[48%]" />
                      <div className="flex-1 bg-[#80b643]/30 rounded-t-md h-[42%]" />
                      <div className="flex-1 bg-[#80b643]/40 rounded-t-md h-[65%]" />
                      <div className="flex-1 bg-[#80b643]/50 rounded-t-md h-[58%]" />
                      <div className="flex-1 bg-[#80b643]/60 rounded-t-md h-[78%]" />
                      <div className="flex-1 bg-[#80b643]/70 rounded-t-md h-[88%]" />
                      <div className="flex-1 bg-[#80b643] rounded-t-md h-full" />

                    </div>

                  </div>


                  {/* Recent Activity */}

                  <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">

                    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">

                      <p className="text-sm font-semibold text-gray-900">
                        Recent Tickets
                      </p>

                      <span className="text-xs text-[#80b643] font-medium">
                        View all
                      </span>

                    </div>


                    <DashboardTicket
                      passenger="John Banda"
                      route="Lusaka → Kitwe"
                      amount="K250"
                    />

                    <DashboardTicket
                      passenger="Mary Phiri"
                      route="Lusaka → Ndola"
                      amount="K300"
                    />

                    <DashboardTicket
                      passenger="Brian Zulu"
                      route="Lusaka → Livingstone"
                      amount="K450"
                      last
                    />

                  </div>

                </div>

              </div>


              {/* Floating POS Card */}

              <div
                className="
                  absolute
                  -bottom-6
                  -left-5
                  sm:-left-8
                  bg-white
                  border border-gray-200
                  rounded-xl
                  shadow-lg
                  p-4
                  flex items-center gap-3
                "
              >

                <div className="w-10 h-10 rounded-xl bg-[#80b643]/10 flex items-center justify-center">
                  <DeviceMobileIcon className="w-5 h-5 text-[#80b643]" />
                </div>

                <div>
                  <p className="text-xs text-gray-500">
                    Mobile POS
                  </p>

                  <p className="text-sm font-semibold text-gray-900">
                    Ready for Sales
                  </p>
                </div>

                <CheckCircleIcon className="w-5 h-5 text-[#80b643]" />

              </div>


              {/* Floating Payment Card */}

              <div
                className="
                  absolute
                  -top-5
                  -right-4
                  sm:-right-6
                  bg-white
                  border border-gray-200
                  rounded-xl
                  shadow-lg
                  p-3
                  flex items-center gap-3
                "
              >

                <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 flex items-center justify-center">
                  <CreditCardIcon className="w-5 h-5 text-[#80b643]" />
                </div>

                <div>
                  <p className="text-[11px] text-gray-500">
                    Payment
                  </p>

                  <p className="text-sm font-semibold text-gray-900">
                    Successful
                  </p>
                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* ===================================================== */}
      {/* TRUST / INTRO */}
      {/* ===================================================== */}

      <section className="py-12 sm:py-14 bg-white border-b border-gray-200">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="max-w-3xl mx-auto text-center">

            <p className="text-sm font-semibold text-[#80b643] uppercase tracking-wider">
              Built for Transport Operators
            </p>

            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-gray-900">
              Everything Your Transport Business Needs
            </h2>

            <p className="mt-4 text-gray-500 leading-relaxed">
              Replace manual paperwork and disconnected systems with a
              centralized platform designed to simplify ticket sales,
              operations and financial management.
            </p>

          </div>

        </div>

      </section>


      {/* ===================================================== */}
      {/* FEATURES */}
      {/* ===================================================== */}

      <section
        id="features"
        className="py-16 md:py-20 bg-gray-50"
      >

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center max-w-2xl mx-auto">

            <p className="text-sm font-semibold text-[#80b643] uppercase tracking-wider">
              Powerful Features
            </p>

            <h2 className="mt-2 text-3xl md:text-4xl font-bold text-gray-900">
              Everything in One POS System
            </h2>

            <p className="mt-4 text-gray-500">
              Give your team the tools they need to manage daily transport
              operations efficiently.
            </p>

          </div>


          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">

            <FeatureCard
              icon={<TicketIcon className="h-6 w-6" />}
              title="Fast Ticket Sales"
              description="Sell tickets quickly through an intuitive POS interface while keeping passenger and journey information organized."
            />

            <FeatureCard
              icon={<ViewGridIcon className="h-6 w-6" />}
              title="Real-Time Seat Management"
              description="View available seats, reserve seats and reduce the risk of double bookings across your ticketing points."
            />

            <FeatureCard
              icon={<CashIcon className="h-6 w-6" />}
              title="Multi-Currency Support"
              description="Operate across different countries and currencies while keeping your ticket sales organized."
            />

            <FeatureCard
              icon={<CreditCardIcon className="h-6 w-6" />}
              title="Multiple Payment Methods"
              description="Accept different payment methods and keep payment records connected to every ticket transaction."
            />

            <FeatureCard
              icon={<ChartBarIcon className="h-6 w-6" />}
              title="Sales & Financial Reports"
              description="Track sales, revenue, costs, gross profit and margins to understand how your business is performing."
            />

            <FeatureCard
              icon={<UserGroupIcon className="h-6 w-6" />}
              title="Employee & POS Management"
              description="Manage employees, roles and permissions while keeping track of who is selling tickets at each POS."
            />

            <FeatureCard
              icon={<RefreshIcon className="h-6 w-6" />}
              title="Shift Management"
              description="Open and close employee shifts, record starting cash and reconcile expected and actual cash."
            />

            <FeatureCard
              icon={<PrinterIcon className="h-6 w-6" />}
              title="Digital & Printed Receipts"
              description="Generate professional receipts and print tickets directly from compatible POS and thermal printers."
            />

            <FeatureCard
              icon={<DeviceMobileIcon className="h-6 w-6" />}
              title="Mobile POS"
              description="Give your ticketing staff a mobile POS experience designed for fast sales at stations and ticketing points."
            />

            <FeatureCard
              icon={<TruckIcon className="h-6 w-6" />}
              title="Routes & Bus Management"
              description="Organize buses, routes, destinations, schedules and ticket prices from one centralized system."
            />

            <FeatureCard
              icon={<DocumentTextIcon className="h-6 w-6" />}
              title="ZRA Smart Invoicing"
              description="Generate and manage ZRA smart invoices directly from your transport POS while keeping transaction records organized."
            />

            <FeatureCard
              icon={<DatabaseIcon className="h-6 w-6" />}
              title="Centralized Data"
              description="Keep your business information organized in one system so authorized users can access the information they need."
            />

          </div>

        </div>

      </section>


      {/* ===================================================== */}
      {/* BENEFITS */}
      {/* ===================================================== */}

      <section
        id="benefits"
        className="py-16 md:py-20 bg-white"
      >

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center max-w-2xl mx-auto">

            <p className="text-sm font-semibold text-[#80b643] uppercase tracking-wider">
              Why TellerPoint
            </p>

            <h2 className="mt-2 text-3xl md:text-4xl font-bold text-gray-900">
              Built to Solve Everyday Transport Challenges
            </h2>

            <p className="mt-4 text-gray-500 leading-relaxed">
              Give your transport business better visibility and control
              across everyday operations.
            </p>

          </div>


          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">

            <BenefitCard
              icon={<ClockIcon className="h-6 w-6" />}
              title="Reduce Manual Work"
              description="Replace paperwork and repetitive processes with a digital ticketing and management workflow."
            />

            <BenefitCard
              icon={<ChartBarIcon className="h-6 w-6" />}
              title="Know Your Numbers"
              description="Get clearer visibility into ticket sales, revenue, costs, cash collections and employee performance."
            />

            <BenefitCard
              icon={<ShieldCheckIcon className="h-6 w-6" />}
              title="Better Control"
              description="Use employee permissions, shift management and centralized records to improve operational control."
            />

          </div>

        </div>

      </section>


      {/* ===================================================== */}
      {/* HOW IT WORKS */}
      {/* ===================================================== */}

      <section className="py-16 md:py-20 bg-gray-50 border-y border-gray-200">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center max-w-2xl mx-auto">

            <p className="text-sm font-semibold text-[#80b643] uppercase tracking-wider">
              Simple Workflow
            </p>

            <h2 className="mt-2 text-3xl md:text-4xl font-bold text-gray-900">
              From Booking to Reporting
            </h2>

            <p className="mt-4 text-gray-500">
              TellerPoint connects the most important parts of your
              transport operation in one workflow.
            </p>

          </div>


          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-5">

            <WorkflowStep
              number="01"
              icon={<TicketIcon className="w-6 h-6" />}
              title="Sell"
              description="Create passenger bookings and sell tickets from your POS."
            />

            <WorkflowStep
              number="02"
              icon={<CreditCardIcon className="w-6 h-6" />}
              title="Collect"
              description="Record cash and supported payment methods against every sale."
            />

            <WorkflowStep
              number="03"
              icon={<PrinterIcon className="w-6 h-6" />}
              title="Issue"
              description="Provide passengers with digital or printed ticket receipts."
            />

            <WorkflowStep
              number="04"
              icon={<ChartBarIcon className="w-6 h-6" />}
              title="Analyze"
              description="Monitor sales, revenue, employees, routes and business performance."
            />

          </div>

        </div>

      </section>


      {/* ===================================================== */}
      {/* CTA / CONTACT */}
      {/* ===================================================== */}

      <section
        id="contact"
        className="relative overflow-hidden bg-[#80b643]"
      >

        <div className="absolute -top-24 -right-24 w-80 h-80 bg-white/10 rounded-full blur-3xl" />

        <div className="absolute -bottom-32 -left-20 w-72 h-72 bg-black/10 rounded-full blur-3xl" />


        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 py-20">

          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/15 mb-5">
            <TruckIcon className="w-7 h-7 text-white" />
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Ready to Modernize Your Transport Business?
          </h2>

          <p className="mt-4 text-white/90 text-base sm:text-lg max-w-2xl mx-auto">
            Start managing your ticket sales, staff, payments and
            operations with TellerPoint.
          </p>


          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">

            <Link
              to="/user/register"
              className="
                inline-flex items-center justify-center gap-2
                px-7 py-3.5
                bg-white
                text-[#80b643]
                rounded-xl
                font-semibold
                shadow-lg
                hover:bg-gray-100
                transition
              "
            >
              Register Your Business
              <ArrowRightIcon className="h-5 w-5" />
            </Link>

            <Link
              to="/login"
              className="
                inline-flex items-center justify-center
                px-7 py-3.5
                bg-white/10
                border border-white/30
                text-white
                rounded-xl
                font-semibold
                hover:bg-white/20
                transition
              "
            >
              Login
            </Link>

          </div>

        </div>

      </section>


      {/* ===================================================== */}
      {/* FOOTER */}
      {/* ===================================================== */}

      <footer
        className="
          bg-gray-950
          text-gray-400
        "
      >

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

            {/* BRAND */}

            <div>

              <div className="mb-5">
                <Logo />
              </div>

              <p className="text-sm leading-relaxed text-gray-400 max-w-xs">
                A modern transport management and POS solution designed to
                help transport companies manage ticket sales, payments,
                employees, routes and business operations.
              </p>

            </div>


            {/* QUICK LINKS */}

            <div>

              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-5">
                Quick Links
              </h3>

              <div className="space-y-3">

                <a
                  href="#features"
                  className="block text-sm hover:text-white transition"
                >
                  Features
                </a>

                <a
                  href="#benefits"
                  className="block text-sm hover:text-white transition"
                >
                  Benefits
                </a>

                <Link
                  to="/user/register"
                  className="block text-sm hover:text-white transition"
                >
                  Register Your Business
                </Link>

                <Link
                  to="/login"
                  className="block text-sm hover:text-white transition"
                >
                  Login
                </Link>

              </div>

            </div>


            {/* CONTACT */}

            <div>

              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-5">
                Contact Us
              </h3>

              <div className="space-y-4">

                <div className="flex items-start gap-3">

                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#80b643]/10 flex items-center justify-center">
                    <MailIcon className="h-5 w-5 text-[#80b643]" />
                  </div>

                  <div>

                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      Email
                    </p>

                    <a
                      href="mailto:info@yourcompany.com"
                      className="text-sm text-gray-300 hover:text-white transition break-all"
                    >
                      pambisichone28@gmail.com
                    </a>

                  </div>

                </div>


                <div className="flex items-start gap-3">

                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#80b643]/10 flex items-center justify-center">
                    <PhoneIcon className="h-5 w-5 text-[#80b643]" />
                  </div>

                  <div>

                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      Phone
                    </p>

                    <a
                      href="tel:+260000000000"
                      className="text-sm text-gray-300 hover:text-white transition"
                    >
                      +260 976 059 080
                    </a>

                  </div>

                </div>

              </div>

            </div>


            {/* LOCATION */}

            <div>

              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-5">
                Our Location
              </h3>

              <div className="flex items-start gap-3">

                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#80b643]/10 flex items-center justify-center">
                  <LocationMarkerIcon className="h-5 w-5 text-[#80b643]" />
                </div>

                <p className="text-sm text-gray-300 leading-relaxed">
                  Libala Mall
                  <br />
                  Lusaka, Zambia
                </p>

              </div>


              <div className="mt-5 flex items-start gap-3">

                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#80b643]/10 flex items-center justify-center">
                  <ClockIcon className="h-5 w-5 text-[#80b643]" />
                </div>

                <div>

                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Business Hours
                  </p>

                  <p className="text-sm text-gray-300 mt-1">
                    Monday – Saturday
                    <br />
                    08:00 – 17:00
                  </p>

                </div>

              </div>

            </div>

          </div>


          {/* BOTTOM BAR */}

          <div className="border-t border-gray-800 py-6">

            <div className="flex flex-col md:flex-row items-center justify-between gap-4">

              <p className="text-xs text-gray-500 text-center md:text-left">
                © {new Date().getFullYear()} TellerPoint. All rights reserved.
              </p>

              <div className="flex items-center gap-5">

                <a
                  href="#features"
                  className="text-xs text-gray-500 hover:text-white transition"
                >
                  Features
                </a>

                <a
                  href="#benefits"
                  className="text-xs text-gray-500 hover:text-white transition"
                >
                  Benefits
                </a>

                <Link
                  to="/login"
                  className="text-xs text-gray-500 hover:text-white transition"
                >
                  Login
                </Link>

              </div>

            </div>

          </div>

        </div>

      </footer>

    </div>
  );
}


/* ===================================================== */
/* TRUST POINT */
/* ===================================================== */

function TrustPoint({ text }) {
  return (
    <div className="flex items-center gap-2">

      <div className="w-5 h-5 rounded-full bg-[#80b643]/10 flex items-center justify-center flex-shrink-0">
        <CheckCircleIcon className="w-4 h-4 text-[#80b643]" />
      </div>

      <span className="text-xs sm:text-sm text-gray-600">
        {text}
      </span>

    </div>
  );
}


/* ===================================================== */
/* DASHBOARD MINI CARD */
/* ===================================================== */

function DashboardMiniCard({
  label,
  value,
  icon,
}) {
  return (
    <div className="border border-gray-200 rounded-xl p-3">

      <div className="flex items-center justify-between">

        <span className="text-gray-400">
          {icon}
        </span>

        <span className="text-[10px] text-gray-400">
          Today
        </span>

      </div>

      <p className="mt-3 text-lg font-bold text-gray-900">
        {value}
      </p>

      <p className="text-[11px] text-gray-500 mt-0.5">
        {label}
      </p>

    </div>
  );
}


/* ===================================================== */
/* DASHBOARD TICKET */
/* ===================================================== */

function DashboardTicket({
  passenger,
  route,
  amount,
  last,
}) {
  return (
    <div
      className={`
        px-4 py-3
        flex items-center justify-between gap-3
        ${!last ? "border-b border-gray-100" : ""}
      `}
    >

      <div className="flex items-center gap-3 min-w-0">

        <div className="w-8 h-8 rounded-lg bg-[#80b643]/10 flex items-center justify-center flex-shrink-0">
          <UserIcon className="w-4 h-4 text-[#80b643]" />
        </div>

        <div className="min-w-0">

          <p className="text-xs font-semibold text-gray-800 truncate">
            {passenger}
          </p>

          <p className="text-[11px] text-gray-500 truncate">
            {route}
          </p>

        </div>

      </div>

      <p className="text-xs font-semibold text-gray-800 whitespace-nowrap">
        {amount}
      </p>

    </div>
  );
}


/* ===================================================== */
/* FEATURE CARD */
/* ===================================================== */

function FeatureCard({
  icon,
  title,
  description,
}) {
  return (
    <div
      className="
        group
        p-6
        bg-white
        border border-gray-200
        rounded-xl
        hover:border-[#80b643]/40
        hover:shadow-md
        transition
        duration-200
      "
    >

      <div
        className="
          w-11 h-11
          flex items-center justify-center
          rounded-xl
          bg-[#80b643]/10
          text-[#80b643]
          group-hover:bg-[#80b643]
          group-hover:text-white
          transition
        "
      >
        {icon}
      </div>


      <h3 className="mt-5 text-base font-semibold text-gray-900">
        {title}
      </h3>


      <p className="mt-2 text-sm text-gray-500 leading-relaxed">
        {description}
      </p>

    </div>
  );
}


/* ===================================================== */
/* BENEFIT CARD */
/* ===================================================== */

function BenefitCard({
  icon,
  title,
  description,
}) {
  return (
    <div
      className="
        flex gap-4
        p-5 sm:p-6
        bg-white
        border border-gray-200
        rounded-xl
        shadow-sm
        hover:border-[#80b643]/40
        hover:shadow-md
        transition
      "
    >

      <div
        className="
          flex-shrink-0
          w-11 h-11
          flex items-center justify-center
          rounded-xl
          bg-[#80b643]/10
        "
      >
        <div className="text-[#80b643]">
          {icon}
        </div>
      </div>


      <div>

        <h3 className="font-semibold text-gray-900">
          {title}
        </h3>

        <p className="mt-1 text-sm text-gray-500 leading-relaxed">
          {description}
        </p>

      </div>

    </div>
  );
}


/* ===================================================== */
/* WORKFLOW STEP */
/* ===================================================== */

function WorkflowStep({
  number,
  icon,
  title,
  description,
}) {
  return (
    <div className="relative">

      <div className="bg-white border border-gray-200 rounded-xl p-6 h-full">

        <div className="flex items-center justify-between">

          <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 flex items-center justify-center text-[#80b643]">
            {icon}
          </div>

          <span className="text-3xl font-bold text-gray-100">
            {number}
          </span>

        </div>

        <h3 className="mt-5 text-base font-semibold text-gray-900">
          {title}
        </h3>

        <p className="mt-2 text-sm text-gray-500 leading-relaxed">
          {description}
        </p>

      </div>

    </div>
  );
}


export default Home;